/**
 * Jonkai Studio - Main App Component
 * Routing and layout configuration
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandProvider } from './contexts/BrandContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { CreateContent } from './pages/CreateContent';
import { CreateGraphic } from './pages/CreateGraphic';
import { Posts } from './pages/Posts';
import { PostEditor } from './pages/PostEditor';
import { CalendarView } from './pages/CalendarView';
import { Analytics } from './pages/Analytics';
import { Settings } from './pages/Settings';
import { ContentSwipe } from './pages/ContentSwipe';
import { BrandDNA } from './pages/BrandDNA';
import { AdCreativeBuilder } from './pages/AdCreativeBuilder';
import { EmailCampaigns } from './pages/EmailCampaigns';
import { LandingPageGenerator } from './pages/LandingPageGenerator';
import { Brands } from './pages/Brands';
import { Home } from './pages/Home';
import { ToastProvider } from './components/ui/ToastProvider';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <Home />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <Layout>
              <CreateContent />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create/graphic"
        element={
          <ProtectedRoute>
            <Layout>
              <CreateGraphic />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/posts"
        element={
          <ProtectedRoute>
            <Layout>
              <Posts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/posts/new"
        element={
          <ProtectedRoute>
            <Layout>
              <PostEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/posts/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <PostEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Layout>
              <CalendarView />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Layout>
              <Analytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/content-queue"
        element={
          <ProtectedRoute>
            <Layout>
              <ContentSwipe />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand-dna"
        element={
          <ProtectedRoute>
            <Layout>
              <BrandDNA />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ad-builder"
        element={
          <ProtectedRoute>
            <Layout>
              <AdCreativeBuilder />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns"
        element={
          <ProtectedRoute>
            <Layout>
              <EmailCampaigns />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/landing-pages"
        element={
          <ProtectedRoute>
            <Layout>
              <LandingPageGenerator />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/brands"
        element={
          <ProtectedRoute>
            <Layout>
              <Brands />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </BrandProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
